import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { ApiReadiness, ReadinessStatus } from '../lib/readiness';
import { Panel } from './Panel';
import { StatusPill, type StatusTone } from './StatusPill';

// The readiness ladder (arc 101 slice 12 step 3). PRESENTER — the caller owns
// the read; this paints the answer and decides nothing.
//
// SCOPE-AGNOSTIC. It renders `ApiReadiness` whatever produced it: the repo
// ladder on RepoDetail, and the ACCOUNT ladder on the onboarding terminal card
// and Home. Only the three chrome strings differ per scope, so they are props,
// and every other string is one shared `readiness.*` key. That namespace used
// to live under `repo_detail.*` — moved here when the second consumer arrived,
// rather than copied, because a second copy of "Blocked" / "Scout-capable box"
// is a translation that can drift against the one enum both surfaces render.
//
// EVERY RUNG IS RENDERED, `ok` ones included. Filtering to the failures would
// throw away the point of the answer: a reader sees the whole remaining path in
// one screen instead of discovering it one refusal at a time, and "this passed"
// and "this was never checked" are different facts that a list of failures
// cannot tell apart. The order is the SERVER's dependency order and is never
// re-sorted here.
//
// NOTHING IS RE-DERIVED. `observed` (the fact) and `detail` (the remedy) are
// server strings and render as-is; only the CHROME is translated. A remedy
// invented in this bundle would be the second implementation this component
// exists to delete.
//
// AND NOTHING IS ACTIONABLE. There is no button on this panel, deliberately:
// exactly one `cleared_by` (`here`) is clearable by an agent over MCP, none of
// the seven is clearable by this SPA, and a control that could only fail reads
// as a bug. The rung says WHERE it clears; the reader goes there.

/**
 * `unknown` must not read as `blocked` — it is "not checked, and here is why",
 * so it takes a tone of its own rather than sharing the failure's. Three
 * statuses, three badge variants (primary / destructive / secondary).
 */
const STATUS_TONE: Record<ReadinessStatus, StatusTone> = {
  ok: 'success',
  blocked: 'danger',
  unknown: 'warning',
};

export type ReadinessLadderProps = {
  readonly ladder: ApiReadiness;
  /** Scope chrome. The rung, status and `cleared_by` strings are NOT props — one shared set. */
  readonly title: string;
  readonly desc: string;
  readonly source: string;
};

export function ReadinessLadder(props: ReadinessLadderProps): JSX.Element {
  return (
    <Panel
      title={props.title}
      action={
        <StatusPill tone={props.ladder.ready ? 'success' : 'warning'}>
          {t(props.ladder.ready ? 'readiness.ready' : 'readiness.not_ready')}
        </StatusPill>
      }
      footer={props.source}
      class="readiness"
    >
      <p class="card__desc">{props.desc}</p>

      {/* `next` is the first non-`ok` rung and is null exactly when ready, so
          this line appears precisely when there is somewhere to go. */}
      <Show when={props.ladder.next}>
        {(next) => (
          <p class="card__desc readiness__next">
            {t('readiness.next', { step: t(`readiness.step_${next()}`) })}
          </p>
        )}
      </Show>

      <ol class="readiness__list" aria-label={t('readiness.list_label')}>
        <For each={props.ladder.steps}>
          {(rung) => (
            <li class="alert">
              <div class="readiness__head">
                <span class="alert__title">{t(`readiness.step_${rung.step}`)}</span>
                <StatusPill tone={STATUS_TONE[rung.status]}>
                  {t(`readiness.status_${rung.status}`)}
                </StatusPill>
              </div>
              <p class="alert__desc">{rung.observed}</p>
              <p class="alert__desc">{rung.detail}</p>
              <p class="alert__desc">
                {t(`readiness.cleared_${rung.cleared_by}`)}
                {/* Non-null EXACTLY when `cleared_by` is `here`, so a rung that
                    clears in a browser or over REST names no verb — there is
                    none, and printing one would send a reader at a call that
                    cannot exist. */}
                <Show when={rung.verb}>
                  {(verb) => (
                    <>
                      {` ${t('readiness.verb')}: `}
                      <span class="data-table__mono">{verb()}</span>
                    </>
                  )}
                </Show>
              </p>
            </li>
          )}
        </For>
      </ol>
    </Panel>
  );
}
